import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { fetchSiteSettings, resolveAssetUrl } from "./api";

export interface SiteSettings {
  siteName: string;
  siteLogo: string;
  siteDescription: string;
  mobileSplashLogo: string;
  mobileAuthLogo: string;
  mobileAuthLogoReuseSplash: boolean;
}

export const DEFAULT_SITE_SETTINGS: SiteSettings = {
  siteName: "大蓝书",
  siteLogo: "/logo.png",
  siteDescription: "在充满共鸣的世界里，记录你的故事",
  mobileSplashLogo: "/logo.png",
  mobileAuthLogo: "/logo.png",
  mobileAuthLogoReuseSplash: true,
};

export const DEFAULT_SITE_SETTING_VALUES: Record<string, string> = {
  siteName: DEFAULT_SITE_SETTINGS.siteName,
  siteLogo: DEFAULT_SITE_SETTINGS.siteLogo,
  siteDescription: DEFAULT_SITE_SETTINGS.siteDescription,
  mobileSplashLogo: DEFAULT_SITE_SETTINGS.mobileSplashLogo,
  mobileAuthLogo: DEFAULT_SITE_SETTINGS.mobileAuthLogo,
  mobileAuthLogoReuseSplash: String(DEFAULT_SITE_SETTINGS.mobileAuthLogoReuseSplash),
};

function normalizeSettings(raw: Record<string, string>): SiteSettings {
  const mobileSplashLogo = raw.mobileSplashLogo || raw.splashIcon || DEFAULT_SITE_SETTINGS.mobileSplashLogo;
  return {
    siteName: raw.siteName || DEFAULT_SITE_SETTINGS.siteName,
    siteLogo: raw.siteLogo || DEFAULT_SITE_SETTINGS.siteLogo,
    siteDescription: raw.siteDescription || DEFAULT_SITE_SETTINGS.siteDescription,
    mobileSplashLogo,
    mobileAuthLogo: raw.mobileAuthLogo || mobileSplashLogo,
    mobileAuthLogoReuseSplash: raw.mobileAuthLogoReuseSplash !== "false",
  };
}

export function getMobileAuthLogo(settings: SiteSettings) {
  return settings.mobileAuthLogoReuseSplash ? settings.mobileSplashLogo : settings.mobileAuthLogo;
}

export function resolveSiteAssetUrl(value: string) {
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  if (value.startsWith("/uploads/") || value.startsWith("uploads/")) return resolveAssetUrl(value);
  return value;
}

const SiteSettingsContext = createContext<SiteSettings>(DEFAULT_SITE_SETTINGS);

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const [rawSettings, setRawSettings] = useState<Record<string, string>>({});
  const settings = useMemo(() => normalizeSettings(rawSettings), [rawSettings]);

  useEffect(() => {
    fetchSiteSettings().then(setRawSettings).catch(() => {
      // Keep the local defaults when the API is unavailable during startup.
    });
  }, []);

  useEffect(() => {
    document.title = settings.siteName;

    const appleTitle = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    if (appleTitle) appleTitle.content = settings.siteName;

    let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement("link");
      favicon.rel = "icon";
      favicon.type = "image/png";
      document.head.appendChild(favicon);
    }
    favicon.href = resolveSiteAssetUrl(settings.siteLogo);
  }, [settings.siteLogo, settings.siteName]);

  return <SiteSettingsContext.Provider value={settings}>{children}</SiteSettingsContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}
